<template>
  <div class="col-12 col-lg-6">
    <div class="row">
      <div class="col-12">
        <div class="pane-header">
          <span class="icon"><icon-colored-demographic /></span>
          <span>{{ capitalize($t('messages.audienceTargeting')) }}</span>
        </div>
      </div>
      <div class="col-12">
        <div class="pane-content pad-top">
          <div class="row">
            <div class="col-12">
              <span id="package_details_demographics">
                <span
                  v-if="deal.demographicTargetIDs?.length === 0"
                  class="text-muted"
                  >{{ $t('messages.allDemographics') }}</span
                >
                <span
                  v-else
                  class="text-muted"
                  >{{ demographicsDescription }}</span
                >
              </span>
              <input
                id="package_details_demographics_value"
                aria-hidden="true"
                type="hidden"
                :value="deal.demographicTargetIDs?.join(',')" />
              <input
                id="package_details_demographics_overridable_value"
                aria-hidden="true"
                type="hidden"
                :value="deal.demographicTargetsOverridable" />
            </div>
          </div>
          <div
            v-if="isNGExternalOrPreferredExternalDeal"
            class="row">
            <div
              v-show="isAdDurationSectionVisible"
              class="col-12"
              :class="isDemandOrganization ? 'col-sm-4' : 'col-sm-6'">
              <h4>{{ capitalize($t('names.concentrationLevel')) }}</h4>
              <span>
                <span
                  v-if="deal.minConcentrationFactor === 1"
                  class="text-muted"
                  >{{ capitalize($t('names.default')) }}</span
                >
                <span
                  v-else-if="deal.minConcentrationFactor === 1.5"
                  class="text-muted"
                  >{{ capitalize($t('messages.highlyConcentrated')) }}</span
                >
                <span
                  v-else-if="deal.minConcentrationFactor === 2"
                  class="text-muted"
                  >{{ capitalize($t('messages.veryHighlyConcentrated')) }}</span
                >
              </span>
              <input
                id="package_details_min_concentration_factor"
                aria-hidden="true"
                type="hidden"
                :value="deal.minConcentrationFactor" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onMounted, ref } from 'vue';

  import { IconColoredDemographic } from '@/common/icons/colored';
  import { FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models';
  import { useDemographics } from '@/org-app/services/tanstack/queries';
  import { queryKeys } from '@/org-app/services/tanstack/queryKeys';
  import { getCachedData, waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import { mapOrgAppGetters } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface IDealDetailsDemographicsProps {
    deal: {
      demographicTargetIDs: number[];
      demographicTargetsOverridable: boolean;
      minConcentrationFactor: number;
    };
    isNGExternalOrPreferredExternalDeal: boolean;
    isAdDurationSectionVisible: boolean;
  }

  const { deal, isNGExternalOrPreferredExternalDeal, isAdDurationSectionVisible } =
    defineProps<IDealDetailsDemographicsProps>();

  const { isDemandOrganization } = mapOrgAppGetters();

  const demographicsQuery = ref(null);

  const allDemographics = computed(() => {
    return demographicsQuery.value?.items || [];
  });

  const demographicsDescription = computed(() => {
    if (allDemographics.value == null || allDemographics.value.length === 0 || deal.demographicTargetIDs.length === 0) {
      capitalize(i18n.global.t('names.all'));
    }
    if (deal.demographicTargetIDs?.filter((x) => allDemographics.value.find((d) => d.id === x) != null).length > 0) {
      return deal.demographicTargetIDs?.map((x) => allDemographics.value.find((d) => d.id === x).name).join(', ');
    }
    return capitalize(i18n.global.t('names.customAudience'));
  });

  onMounted(async () => {
    if (deal.demographicTargetIDs.length === 0) return;
    const cachedData = getCachedData([queryKeys.demographics]);
    demographicsQuery.value = useDemographics(
      undefined,
      !cachedData
        ? {
            queryParams: QueryParams.all(
              FilterCollection.for('demographic_id', deal.demographicTargetIDs, FilterComparisonOperator.IN),
            ),
          }
        : {},
    );
    await waitForPendingQueries([demographicsQuery.value]);
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;
</style>
